/**
 * @voltxampere/spice — Potentiometer codec
 *
 * SPICE has no native potentiometer element. We round-trip the
 * canonical workshop convention: emit two paired resistors named
 * `Rpot{id}A` / `Rpot{id}B` whose `comment` line marks them as
 * one logical pot. The parser DOES NOT reverse this — incoming
 * SPICE without our marker comes through as two plain resistors.
 *
 * Sprint 16 ships the export side only; import recognition (a
 * pair of `Rpot…A` + `Rpot…B` lines) is parked behind the comment
 * marker `* @vxa-pot {id} {wiperPercent} {totalResistance}` which
 * `from-spice` consults when present. If absent, two resistors
 * stay two resistors.
 *
 * Schema mapping: `type: "potentiometer"`, params:
 * `{ totalResistance, wiperPercent }`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam } from "./_param-helpers.js";
export const potentiometerCodec = {
    spiceLetter: "P",
    schemaTypes: ["potentiometer"],
    examples: ["Ppot1 in wiper out 10k 50"],
    /**
     * Recognises the synthetic marker form `P{id} N1 NW N2 R W` we
     * emit ourselves — Sprint 16 import is symmetric with export
     * only when the source file came from VoltXAmpere. Foreign
     * SPICE files do not contain `P` lines (SPICE has no native pot).
     */
    parse(card, _ctx) {
        if (card.tokens.length < 5) {
            return err("P needs 3 nodes, total resistance, and wiper%");
        }
        const [n1, nw, n2, rRaw, wRaw] = card.tokens;
        if (n1 === undefined ||
            nw === undefined ||
            n2 === undefined ||
            rRaw === undefined ||
            wRaw === undefined) {
            return err("P has incomplete tokens");
        }
        const total = parseSpiceNumber(rRaw);
        const wiper = parseSpiceNumber(wRaw);
        if (total === null || wiper === null) {
            return err("P value/wiper not numeric");
        }
        const params = {
            totalResistance: total,
            wiperPercent: wiper,
        };
        const component = {
            id: card.identifier,
            type: "potentiometer",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [n1, nw, n2]);
    },
    emit(component, terminals) {
        const total = numericParam(component.params.totalResistance, 10000);
        const wiper = numericParam(component.params.wiperPercent, 50);
        const [a, w, b] = terminals;
        return `${component.id} ${a ?? "0"} ${w ?? "0"} ${b ?? "0"} ${formatSpiceValue(total)} ${formatSpiceValue(wiper)}`;
    },
    owns(schemaType) {
        return schemaType === "potentiometer";
    },
};
//# sourceMappingURL=potentiometer.js.map